import { useEffect, useState } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import WatchlistBuilderPane from "./WatchlistBuilderPane";
import DescribeItPanel from "./DescribeItPanel";
import type { WatchlistDraft } from "./DescribeItPanel";
import BuilderStart from "./BuilderStart";
import YourWatchlists from "./YourWatchlists";
import NewsquawkLibraryList from "./NewsquawkLibraryList";
import { inspectTargetOf, type InspectTarget } from "../listEntries";
import WatchlistTermsModal from "@/ui-components/FilterPanel/WatchlistTermsModal";
import { conciergeEnabled } from "@/onboarding/concierge/api";
import { useWatchlistPush } from "@/demo-guide/useWatchlistPush";
import { useSearchParams } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import type {
  UseWatchlistCreatorReturn,
  EditableWatchlist,
  SavedWatchlist,
} from "../hooks/useWatchlistCreator";
import type { FilterDraft } from "../hooks/useFilterBuilder";

interface BuilderCanvasProps {
  creator: UseWatchlistCreatorReturn;
  onOpenInBuilder: (draft: FilterDraft) => void;
  /**
   * Optional, and separate from onOpenInBuilder on purpose: the concierge
   * route tolerates a no-op default, but the Advanced Builder link must not
   * render at all where there is nowhere for it to go.
   */
  onOpenAdvanced?: (draft: FilterDraft) => void;
  /** Seeds the builder from a single-watchlist concierge plan. */
  onOpenWatchlist?: (draft: WatchlistDraft) => void;
  /**
   * After anything is saved or created here — a watchlist, a combination, or
   * whatever the concierge made. `saved` is absent when there is no single
   * record to point at (a concierge plan, the guided demo's combination).
   */
  onSaved: (saved?: SavedWatchlist) => void;
  onCancel: () => void;
  onEditWatchlist: (item: EditableWatchlist) => void;
  /** A library list, opened in the builder as an editable copy. */
  onBuildOn: (item: EditableWatchlist) => void;
}

/**
 * The watchlist builder.
 *
 * One column that changes with the job. On arrival: the two ways to start a
 * list, and underneath them your lists beside the Newsquawk library, newest of
 * yours first under "Recently created". Once you have chosen a way in, the
 * form (or the concierge) takes the whole width; saving brings you back to the
 * lists, with what you just made at the top.
 *
 * Your lists used to sit in a rail on the right, as reference for the form.
 * But the form is gone once you save, so the landing was two buttons beside a
 * narrow column you had to scan to find out whether the save had worked.
 */
export default function BuilderCanvas({
  creator,
  onOpenInBuilder,
  onOpenAdvanced,
  onOpenWatchlist,
  onSaved,
  onCancel,
  onEditWatchlist,
  onBuildOn,
}: BuilderCanvasProps) {
  const { authenticated } = useAuth();
  const [manualOpen, setManualOpen] = useState(false);

  /** Logged-out visitor sent here by the guided demo's last step. */
  const watchlistPush = useWatchlistPush();

  /**
   * ?describe=1 opens the assisted route, as if the card had been pressed.
   *
   * No longer used by the guided demo, which now asks the visitor to press the
   * card themselves — a rail button that opened this panel did nothing at all
   * on a second press, and doing it FOR them meant the run could complete
   * without the product being touched. See BUILD_ON_BUILDER.
   *
   * Kept as a deep link: it is the only way to land straight on the assisted
   * route, which is worth having for testing and for linking people to it.
   * Same shape as the ?create= handling in PortfolioBuilderBrowser: act once,
   * then clear the param so a refresh or a back-navigation does not
   * re-trigger it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("describe") !== "1") return;
    setDescribeOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("describe");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [describeOpen, setDescribeOpen] = useState(false);

  const [inspecting, setInspecting] = useState<InspectTarget | null>(null);
  const inspect = (item: EditableWatchlist) =>
    setInspecting(inspectTargetOf(item));

  const hasContent =
    creator.name.trim().length > 0 ||
    creator.tickers.length > 0 ||
    creator.keywords.length > 0 ||
    creator.selectedTags.length > 0 ||
    creator.importedWatchlistIds.size > 0;
  // Opening the form is an explicit step: pressing "Build it myself", or
  // arriving with content already in hand (an edit, or a concierge draft).
  const building = manualOpen || hasContent;

  const onLanding = !building && !describeOpen;

  /**
   * When the landing was last left for the form or the concierge.
   *
   * Anything created after it is what this visit to the form produced, and is
   * the "just made" highlight when the list comes back. A time rather than an
   * id because the concierge can make several lists in one go, and all of
   * them are the answer to what the person just did.
   */
  const [leftLandingAt, setLeftLandingAt] = useState<number | null>(null);
  useEffect(() => {
    if (!onLanding) setLeftLandingAt(Date.now());
  }, [onLanding]);

  /**
   * Every save ends back on the landing, whichever route made it.
   *
   * Before, it depended on the route: an edit closed, a new watchlist left the
   * filled form on screen (inviting a second save of the same list), a
   * concierge draft reopened the empty concierge, and a combination jumped to
   * the library view.
   */
  const finishSave = (saved?: SavedWatchlist) => {
    setManualOpen(false);
    setDescribeOpen(false);
    creator.reset();
    onSaved(saved);
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {!building && describeOpen ? (
        /* The assisted route gets the same full container as the manual one,
           rather than being squeezed into the half-width card it was
           launched from. What it produces then lands in the ordinary
           builder, pre-filled — see `onOpenWatchlist`. */
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <div className="w-full px-5 py-5">
            <div className="flex items-center gap-2">
              <SparklesIcon className="size-4 shrink-0 text-gray-400" />
              <h2 className="flex-1 text-xs font-semibold uppercase tracking-[0.1em] text-gray-300">
                Build it for me
              </h2>
              <button
                type="button"
                onClick={() => setDescribeOpen(false)}
                className="cursor-pointer text-xs text-gray-500 transition-colors hover:text-gray-300"
              >
                Cancel
              </button>
            </div>
            <div className="mt-4">
              <DescribeItPanel
                variant="hero"
                onOpenInBuilder={onOpenInBuilder}
                onOpenWatchlist={onOpenWatchlist}
                onCreated={() => finishSave()}
              />
            </div>
          </div>
        </div>
      ) : !building ? (
        /* The list takes the height left under the cards and scrolls inside
           itself, as the widget menus do. The page scrolls only when that
           leaves too little room for the list to be useful. */
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          <BuilderStart
            onManual={() => setManualOpen(true)}
            onDescribe={conciergeEnabled ? () => setDescribeOpen(true) : undefined}
            demoPush={watchlistPush}
          />
          {/* A logged-out visitor can't own a list, so there is nothing to
              show — and an empty panel under the demo's two cards would only
              compete with the one it is asking them to press. */}
          {authenticated && (
            <div className="flex min-h-[22rem] flex-1 flex-col px-5 pb-5">
              {/* Two columns on the same grid as the cards above, so the
                  width beside your lists holds the other half of the job
                  instead of standing empty. */}
              <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
                <YourWatchlists
                  portfolios={creator.portfolios}
                  onOpen={onEditWatchlist}
                  onInspect={inspect}
                  highlightSince={leftLandingAt}
                />
                <NewsquawkLibraryList
                  portfolios={creator.portfolios}
                  onBuildOn={onBuildOn}
                  onEdit={onEditWatchlist}
                  onInspect={inspect}
                />
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No describe box here. The two ways in are a choice made on the way
           in, not a thing to re-offer above the form you already chose —
           sitting it on top of the manual builder made the page read as one
           path with a second one nagging over it. */
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
          {/* Cancel closes the form as well as emptying it: `building` is
              `manualOpen || hasContent`, so clearing content alone left the
              first half true and the form on screen. */}
          <WatchlistBuilderPane
            creator={creator}
            onOpenAdvanced={onOpenAdvanced}
            onSaved={finishSave}
            onCancel={() => {
              setManualOpen(false);
              setDescribeOpen(false);
              onCancel();
            }}
          />
        </div>
      )}

      {/* The menus' look inside a list, so the icon does here what it does
          there. */}
      <WatchlistTermsModal
        id={inspecting?.id ?? null}
        sectionId={inspecting?.sectionId}
        onClose={() => setInspecting(null)}
      />
    </div>
  );
}
